import {
  DropdownMenuContent,
  DropdownMenuItemWithSecondaryAction,
  DropdownMenuSeparator,
  DropdownMenuLoadingItem,
} from "@/src/components/ui/dropdown-menu";
import { createProjectRoute } from "@/src/features/setup/setupRoutes";
import { PlusIcon, Settings } from "lucide-react";
import { type Session } from "next-auth";
import { EntityMark } from "@/src/components/nav/entity-mark";

type Project = NonNullable<
  Session["user"]
>["organizations"][number]["projects"][number];

type ProjectDropdownMenuProps = {
  organizationId: string;
  /** The project being viewed; highlighted in the menu. */
  currentProjectId?: string;
  canCreateProjects: boolean;
  getProjectPath: (projectId: string) => string;
} & (
  | { state: "loading" }
  | {
      state: "loaded";
      projects: Project[];
    }
);

export function ProjectDropdownMenu(props: ProjectDropdownMenuProps) {
  const {
    organizationId,
    currentProjectId,
    canCreateProjects,
    getProjectPath,
  } = props;

  return (
    <DropdownMenuContent
      align="start"
      header="Projects"
      maxHeight="20rem"
      className="w-64"
    >
      {props.state === "loaded" ? (
        props.projects.map((dropdownProject) => (
          <DropdownMenuItemWithSecondaryAction
            key={dropdownProject.id}
            title={dropdownProject.name}
            leading={<EntityMark name={dropdownProject.name} />}
            description={formatRole(dropdownProject.role)}
            selected={dropdownProject.id === currentProjectId}
            href={getProjectPath(dropdownProject.id)}
            secondaryAction={{
              href: `/project/${dropdownProject.id}/settings`,
              ariaLabel: `Go to settings for ${dropdownProject.name}`,
              icon: Settings,
            }}
          />
        ))
      ) : (
        <>
          <DropdownMenuLoadingItem />
          <DropdownMenuLoadingItem />
          <DropdownMenuLoadingItem />
        </>
      )}

      {canCreateProjects && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItemWithSecondaryAction
            title="New Project"
            href={createProjectRoute(organizationId)}
            icon={PlusIcon}
          />
        </>
      )}
    </DropdownMenuContent>
  );
}

// "VIEWER" → "Viewer"; NONE-role grant holders have no role to show.
function formatRole(role: Project["role"]): string | undefined {
  if (!role || role === "NONE") return undefined;
  return role.charAt(0) + role.slice(1).toLowerCase();
}
